import { useEffect, useRef } from 'react';
import { BASE_URL } from '../config/Config';

/**
 * Extract host and port from BASE_URL for WebSocket connection
 */
function getWebSocketHost() {
  try {
    const url = new URL(BASE_URL);
    return url.host;
  } catch (e) {
    if (BASE_URL.includes('://')) {
      const url = new URL(BASE_URL);
      return url.host;
    }
    if (BASE_URL.includes(':')) {
      return BASE_URL.replace(/^https?:\/\//, '').replace(/\/$/, '');
    }
    return window.location.host;
  }
}

/**
 * Custom hook for managing WebSocket connection for student notifications.
 * 
 * This hook establishes a WebSocket connection to receive real-time nudge
 * messages from teachers/parents. It automatically reconnects on disconnect
 * and handles authentication via token (matching existing API auth).
 * 
 * @param {Function} onMessage - Callback function called when a message is received
 * @param {Object} options - Optional configuration
 * @param {number} options.reconnectInterval - Milliseconds to wait before reconnecting (default: 5000)
 * @param {boolean} options.enabled - Whether to enable the WebSocket connection (default: true)
 */
export function useStudentNotifications(onMessage, options = {}) {
  const {
    reconnectInterval = 5000,
    enabled = true,
  } = options;

  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const enabledRef = useRef(enabled);
  const onMessageRef = useRef(onMessage);

  // Keep refs up to date
  useEffect(() => {
    enabledRef.current = enabled;
    onMessageRef.current = onMessage;
  }, [enabled, onMessage]);

  useEffect(() => {
    if (!enabledRef.current) {
      return;
    }

    let ws;
    const wsHost = getWebSocketHost();
    const protocol = BASE_URL.startsWith('https://') || window.location.protocol === 'https:' ? 'wss:' : 'ws:';

    function connect() {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      if (!enabledRef.current) {
        return;
      }

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      // Only connect when authenticated (token present); backend rejects unauthenticated connections
      if (!token) {
        return;
      }

      try {
        const url = `${protocol}//${wsHost}/ws/notifications/?token=${encodeURIComponent(token)}`;
        ws = new WebSocket(url);

        ws.onopen = () => {
          wsRef.current = ws;
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (onMessageRef.current) {
              onMessageRef.current(data);
            }
          } catch (err) {
            console.error('[WebSocket] Failed to parse message:', err);
          }
        };

        ws.onerror = (error) => {
          console.error('[WebSocket] Connection error:', error);
        };

        ws.onclose = (event) => {
          wsRef.current = null;
          // Don't retry on normal close (1000) or auth rejection (4001=unauthorized, 4003=forbidden)
          const authRejection = event.code === 4001 || event.code === 4003;
          if (enabledRef.current && event.code !== 1000 && !authRejection) {
            reconnectTimeoutRef.current = setTimeout(() => {
              if (enabledRef.current) {
                connect();
              }
            }, reconnectInterval);
          }
        };

      } catch (error) {
        console.error('[WebSocket] Failed to create WebSocket connection:', error);
        if (enabledRef.current) {
          reconnectTimeoutRef.current = setTimeout(() => {
            if (enabledRef.current) {
              connect();
            }
          }, reconnectInterval);
        }
      }
    }

    // Initial connection
    connect();

    // Cleanup function
    return () => {
      // Clear reconnect timeout
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      // Close WebSocket connection
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmounted');
        wsRef.current = null;
      } else if (ws) {
        ws.close(1000, 'Component unmounted');
      }
    };
  }, [enabled, reconnectInterval]); // Re-run when enabled or reconnectInterval changes

  // Return connection status helper
  return {
    isConnected: wsRef.current?.readyState === WebSocket.OPEN,
    reconnect: () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    }
  };
}

